import { Link } from 'react-router-dom';
import SeoHead from './SeoHead';
import { Breadcrumbs } from './ComponentLibrary';
import { pageMeta } from '../seoUtils';
import { ADMIN_FEATURE_FLAGS } from '../config/adminFeatureFlags';

function PrivacyPage() {
  const seo = pageMeta({
    title: 'Privacy Policy',
    description:
      "Read NaijaNest's privacy policy. Learn what personal information we collect, how we use it, who we share it with, and your rights.",
    path: '/privacy',
  });
  return (
    <div className="static-page privacy-page">
      <SeoHead title={seo.fullTitle} description={seo.desc} url={seo.url} image={seo.img} />
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'Privacy Policy', current: true },
        ]}
      />
      <section className="section">
        <header className="privacy-page__intro">
          <h1>Privacy Policy</h1>
          <p className="static-page__lead">
            Effective date: 2 August 2026. Last updated: 2 August 2026.
          </p>
        </header>

        <p>
          This Privacy Policy explains what personal information NaijaNest collects, how we use
          it, who we share it with, and the choices you have. It applies to everyone who uses the
          NaijaNest website and app (the &quot;Platform&quot;).
        </p>

        <h2>Information We Collect</h2>
        <ul>
          <li>
            <strong>Account information</strong>: name and email address when you register.
          </li>
          <li>
            <strong>Listings</strong>: property details, descriptions, pricing, images, and a
            contact phone number, when you create a listing.
          </li>
          <li>
            <strong>Identity verification documents</strong>: if you apply to become an agent, we
            collect a government-issued ID, proof of address, and business/CAC details where
            applicable. These are used only to review your application and are handled with extra
            care.
          </li>
          <li>
            <strong>Booking information</strong>: check-in and check-out dates, number of guests,
            and related details when you make or receive a short-let booking.
          </li>
          <li>
            <strong>Payment information</strong>: when you make a payment (for example, a booking
            or an advertising placement), our payment processor, Paystack, handles your card or
            bank details directly. NaijaNest does not receive or store your full card or bank
            account details, only confirmation of the transaction (amount, reference, and status).
          </li>
          <li>
            <strong>Messages</strong>: content you send through NaijaNest Messages to communicate
            with other users about a listing.
          </li>
          <li>
            <strong>Usage data</strong>: pages visited and search queries, to improve our service.
          </li>
        </ul>

        <h2>How We Use Your Information</h2>
        <ul>
          <li>To display and manage property listings</li>
          <li>To connect seekers with agents and landlords</li>
          <li>To process bookings and payments</li>
          <li>To review agent applications and verify submitted documents</li>
          <li>To send property alerts and account or transaction notifications</li>
          <li>To detect and prevent fraud</li>
        </ul>

        <h2>How We Share Your Information</h2>
        <p>We do not sell your personal data. We share information only where necessary:</p>
        <ul>
          <li>
            <strong>Other users</strong>: your contact info is only shared when you initiate
            contact with a lister, or when someone contacts you about your own listing.
          </li>
          <li>
            <strong>Paystack</strong>, to process payments securely.
          </li>
          <li>
            <strong>Google</strong>, for address lookup and maps (Google Places/Maps), sign-in
            (Google Sign-In), and, where you&rsquo;ve consented, analytics (Google Analytics,
            Firebase Analytics). See our <Link to="/cookie-policy">Cookie Policy</Link> for detail
            on which of these load and when.
          </li>
          <li>
            Law enforcement or regulators, where required by law or to protect the safety of our
            users.
          </li>
        </ul>

        <h2>Data Retention</h2>
        <p>
          We keep personal information for as long as your account is active or as needed to
          provide the Platform, resolve disputes, and meet legal obligations. Identity
          verification documents are retained only for as long as needed to process and review
          your application, and are not reused for any other purpose.
        </p>

        <h2 id="cookies">Cookie Policy</h2>
        <p>
          We use a few categories of cookies, some essential and some optional (analytics and
          convenience features). See our <Link to="/cookie-policy">Cookie Policy</Link> page for
          the full breakdown and to manage your preferences.
        </p>

        <h2>Your Rights</h2>
        <p>
          You can access, correct, or delete your data, including uploaded verification
          documents, at any time. Email{' '}
          <a href="mailto:hello@naijanestproperties.com">hello@naijanestproperties.com</a>.
        </p>
        <p>
          NaijaNest is operated by NaijaNest Prime Hub, a company registered in Nigeria (RC
          9619830).
        </p>

        <h2>Related Policies</h2>
        <p>Please also review our:</p>
        <ul>
          <li>
            <Link to="/terms">Terms of Service</Link>
          </li>
          <li>
            <Link to="/cookie-policy">Cookie Policy</Link>
          </li>
          <li>
            <Link to="/listing-policy">Listing Policy</Link>
          </li>
          {ADMIN_FEATURE_FLAGS.agentRequests && (
            <li>
              <Link to="/agent-verification-policy">Agent Verification Policy</Link>
            </li>
          )}
          <li>
            <Link to="/safety">Safety Centre</Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
export default PrivacyPage;
